/** @file Floating DOM toolbar: tool modes, color palette, undo/redo, clear-all. */
import { appStore } from "../core/state.js";
import { DRAW_COLORS } from "../core/play.js";
import { execute, undo, redo, canUndo, canRedo, onChange as onHistoryChange } from "../core/history.js";
import {
  clearDrawingsCommand,
  deleteDrawingCommand,
  setColorCommand,
} from "../core/commands.js";
import { setTool } from "../input/tool-fsm.js";
import { authStore } from "../core/auth-store.js";
import { currentPlayStore, setCurrentPlay } from "../core/current-play.js";
import { stateToPlay } from "../core/play-serializer.js";
import { captureThumbnail } from "../core/thumbnail.js";
import { getStage } from "../render/stage.js";
import { createPlay, updatePlay } from "../persistence/plays-repo.js";
import { listTeams } from "../persistence/teams-repo.js";
import { applyTeamToTokens } from "../core/team-apply.js";
import { openSaveDialog } from "./save-dialog.js";
import { openTeamApplyDialog } from "./team-apply-dialog.js";
import { showToast } from "./toast.js";
import { mapAuthError } from "../persistence/auth.js";
import { mountShareDialog } from "./share-dialog.js";
import { resetEditorToNewPlay } from "../core/new-play.js";
import { confirmDiscardDirtyEditor, markEditorClean } from "../core/editor-dirty.js";

/** @typedef {import("../core/play.js").ToolMode} ToolMode */

/** @type {{ id: ToolMode, label: string, icon: string }[]} */
const TOOLS = [
  { id: "select", label: "Auswählen", icon: "↖" },
  { id: "run", label: "Laufweg", icon: "→" },
  { id: "pass", label: "Pass", icon: "⇢" },
  { id: "shot", label: "Schuss", icon: "⚡" },
  { id: "erase", label: "Löschen", icon: "✕" },
];

/** @type {HTMLDivElement | null} */
let root = null;
/** @type {Map<ToolMode, HTMLButtonElement>} */
const toolButtons = new Map();
/** @type {Map<string, HTMLButtonElement>} */
const colorButtons = new Map();
/** @type {HTMLButtonElement | null} */
let undoBtn = null;
/** @type {HTMLButtonElement | null} */
let redoBtn = null;
/** @type {HTMLButtonElement | null} */
let deleteBtn = null;
/** @type {HTMLButtonElement | null} */
let clearBtn = null;
/** @type {HTMLButtonElement | null} */
let saveBtn = null;
/** @type {HTMLButtonElement | null} */
let saveAsNewBtn = null;
/** @type {HTMLButtonElement | null} */
let applyTeamBtn = null;
/** @type {HTMLButtonElement | null} */
let newPlayBtn = null;
/** @type {HTMLButtonElement | null} */
let shareBtn = null;
/** @type {(() => void) | null} */
let unsubAuth = null;
/** @type {(() => void) | null} */
let unsubStore = null;
/** @type {(() => void) | null} */
let unsubHistory = null;

/**
 * @param {HTMLElement} parent
 */
export function mountToolbar(parent) {
  if (root) {
    return;
  }
  root = document.createElement("div");
  root.className = "hb-toolbar";
  root.setAttribute("role", "toolbar");
  root.setAttribute("aria-label", "Taktikboard-Werkzeuge");

  // Tool group
  const toolGroup = document.createElement("div");
  toolGroup.className = "hb-toolbar__group";
  for (const tool of TOOLS) {
    const btn = button(`${tool.icon}`, tool.label);
    btn.dataset.tool = tool.id;
    btn.addEventListener("click", () => setTool(tool.id));
    toolButtons.set(tool.id, btn);
    toolGroup.appendChild(btn);
  }
  root.appendChild(toolGroup);

  // Color group
  const colorGroup = document.createElement("div");
  colorGroup.className = "hb-toolbar__group";
  for (const color of DRAW_COLORS) {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "hb-color";
    btn.style.setProperty("--swatch", color);
    btn.setAttribute("aria-label", `Farbe ${color}`);
    btn.addEventListener("click", () => {
      const current = appStore.get().drawColor;
      if (current !== color) {
        execute(setColorCommand(current, color));
      }
    });
    colorButtons.set(color, btn);
    colorGroup.appendChild(btn);
  }
  root.appendChild(colorGroup);

  // History group
  const historyGroup = document.createElement("div");
  historyGroup.className = "hb-toolbar__group";
  undoBtn = button("↺", "Rückgängig (Ctrl+Z)");
  undoBtn.addEventListener("click", () => undo());
  redoBtn = button("↻", "Wiederherstellen (Ctrl+Shift+Z)");
  redoBtn.addEventListener("click", () => redo());
  historyGroup.appendChild(undoBtn);
  historyGroup.appendChild(redoBtn);
  root.appendChild(historyGroup);

  // Action group
  const actionGroup = document.createElement("div");
  actionGroup.className = "hb-toolbar__group";
  deleteBtn = button("🗑", "Ausgewählten Pfeil löschen");
  deleteBtn.addEventListener("click", () => {
    const id = appStore.get().selectedDrawingId;
    if (!id) {
      return;
    }
    const cmd = deleteDrawingCommand(id);
    if (cmd) {
      execute(cmd);
    }
  });
  clearBtn = button("Clr", "Alle Pfeile löschen");
  clearBtn.addEventListener("click", () => {
    const cmd = clearDrawingsCommand();
    if (cmd) {
      execute(cmd);
    }
  });
  actionGroup.appendChild(deleteBtn);
  actionGroup.appendChild(clearBtn);
  root.appendChild(actionGroup);

  // Persistence group
  const saveGroup = document.createElement("div");
  saveGroup.className = "hb-toolbar__group";
  newPlayBtn = button("Neu", "Neuen Spielzug erstellen");
  newPlayBtn.addEventListener("click", () => {
    if (!confirmDiscardDirtyEditor()) {
      return;
    }
    resetEditorToNewPlay();
    showToast("Neuer Spielzug bereit");
  });
  saveGroup.appendChild(newPlayBtn);
  saveBtn = button("Speichern", "Spielzug speichern (Ctrl+S)");
  saveBtn.addEventListener("click", () => {
    handleSave().catch((err) => console.warn("[save]", err));
  });
  saveGroup.appendChild(saveBtn);
  saveAsNewBtn = button("Als neu", "Aktuelles Board als neuen Spielzug speichern");
  saveAsNewBtn.addEventListener("click", () => {
    handleSave({ forceCreate: true }).catch((err) => console.warn("[save-as-new]", err));
  });
  saveGroup.appendChild(saveAsNewBtn);
  applyTeamBtn = button("Team", "Team auf aktuellen Spielzug anwenden");
  applyTeamBtn.addEventListener("click", () => {
    handleApplyTeam().catch((err) => console.warn("[team-apply]", err));
  });
  saveGroup.appendChild(applyTeamBtn);
  shareBtn = button("🔗", "Spielzug teilen");
  shareBtn.dataset.action = "share";
  shareBtn.addEventListener("click", () => {
    handleShare().catch((err) => console.warn("[share]", err));
  });
  saveGroup.appendChild(shareBtn);
  root.appendChild(saveGroup);

  parent.appendChild(root);

  unsubStore = appStore.subscribe(() => refresh());
  unsubAuth = authStore.subscribe(() => refresh());
  unsubHistory = onHistoryChange(() => refresh());

  window.addEventListener("keydown", keyboardHandler);
  refresh();
}

export function unmountToolbar() {
  unsubStore?.();
  unsubHistory?.();
  unsubAuth?.();
  unsubStore = null;
  unsubHistory = null;
  unsubAuth = null;
  saveBtn = null;
  saveAsNewBtn = null;
  applyTeamBtn = null;
  newPlayBtn = null;
  shareBtn = null;
  window.removeEventListener("keydown", keyboardHandler);
  root?.remove();
  root = null;
  toolButtons.clear();
  colorButtons.clear();
  undoBtn = null;
  redoBtn = null;
  deleteBtn = null;
  clearBtn = null;
}

/**
 * @param {string} label
 * @param {string} title
 * @returns {HTMLButtonElement}
 */
function button(label, title) {
  const btn = document.createElement("button");
  btn.type = "button";
  btn.className = "hb-btn";
  btn.textContent = label;
  btn.title = title;
  btn.setAttribute("aria-label", title);
  return btn;
}

function refresh() {
  const state = appStore.get();
  for (const [id, btn] of toolButtons) {
    btn.classList.toggle("hb-btn--active", state.tool === id);
  }
  for (const [color, btn] of colorButtons) {
    btn.classList.toggle("hb-color--active", state.drawColor === color);
  }
  if (undoBtn) {
    undoBtn.disabled = !canUndo();
  }
  if (redoBtn) {
    redoBtn.disabled = !canRedo();
  }
  if (deleteBtn) {
    deleteBtn.disabled = !state.selectedDrawingId;
  }
  if (clearBtn) {
    clearBtn.disabled = state.drawings.length === 0;
  }
  if (saveBtn) {
    const signedIn = authStore.get().status === "signedIn";
    saveBtn.disabled = !signedIn;
    saveBtn.title = signedIn
      ? "Spielzug speichern (Ctrl+S)"
      : "Zum Speichern anmelden";
  }
  if (saveAsNewBtn) {
    const signedIn = authStore.get().status === "signedIn";
    saveAsNewBtn.disabled = !signedIn;
    saveAsNewBtn.title = signedIn
      ? "Aktuelles Board als neuen Spielzug speichern"
      : "Zum Speichern anmelden";
  }
  if (applyTeamBtn) {
    const signedIn = authStore.get().status === "signedIn";
    applyTeamBtn.disabled = !signedIn;
    applyTeamBtn.title = signedIn
      ? "Team auf aktuellen Spielzug anwenden"
      : "Zum Anwenden anmelden";
  }
  if (shareBtn) {
    const signedIn = authStore.get().status === "signedIn";
    const current = currentPlayStore.get().current;
    const canShare = signedIn && !!current?.playId;
    shareBtn.disabled = !canShare;
    shareBtn.title = canShare
      ? "Spielzug teilen"
      : signedIn
        ? "Zum Teilen zuerst speichern"
        : "Zum Teilen anmelden";
  }
}

async function handleShare() {
  const authState = authStore.get();
  if (authState.status !== "signedIn" || !authState.user) {
    return;
  }
  const current = currentPlayStore.get().current;
  if (!current) {
    showToast("Bitte zuerst speichern");
    return;
  }
  await mountShareDialog({
    ownerId: authState.user.uid,
    playId: current.playId,
    playName: current.name,
  });
}

async function handleApplyTeam() {
  const authState = authStore.get();
  if (authState.status !== "signedIn" || !authState.user) {
    return;
  }
  /** @type {import("../types/schemas.js").TeamDoc[]} */
  let teams = [];
  try {
    teams = await listTeams(authState.user.uid);
  } catch (e) {
    console.warn("[team-apply] listTeams failed", e);
    showToast("Teams konnten nicht geladen werden");
    return;
  }
  if (teams.length === 0) {
    showToast("Noch kein Team angelegt");
    return;
  }
  const teamId = await openTeamApplyDialog({ teams });
  if (!teamId) {
    return;
  }
  const team = teams.find((candidate) => candidate.id === teamId);
  if (!team) {
    showToast("Team nicht gefunden");
    return;
  }
  const state = appStore.get();
  appStore.set({ tokens: applyTeamToTokens(state.tokens, team) });
  showToast(`Team ${team.name} angewendet`);
}

/** @param {{ forceCreate?: boolean }} [opts] */
async function handleSave(opts = {}) {
  const authState = authStore.get();
  if (authState.status !== "signedIn" || !authState.user) {
    return;
  }
  const uid = authState.user.uid;
  const { current } = currentPlayStore.get();
  const stage = getStage();
  const state = appStore.get();
  /** @type {string | undefined} */
  let thumbnail;
  try {
    thumbnail = stage
      ? captureThumbnail(/** @type {any} */ (stage))
      : undefined;
  } catch (e) {
    console.warn("[save] thumbnail capture failed", e);
  }

  try {
    if (current && !opts.forceCreate) {
      const payload = stateToPlay(
        { name: current.name, teamId: current.teamId, ...(thumbnail ? { thumbnail } : {}) },
        state,
      );
      await updatePlay(uid, current.playId, /** @type {any} */ (payload));
      markEditorClean();
      showToast("Spielzug aktualisiert");
      return;
    }

    /** @type {import("../types/schemas.js").TeamDoc[]} */
    let teams = [];
    try {
      teams = await listTeams(uid);
    } catch (e) {
      console.warn("[save] listTeams failed, continuing without", e);
    }
    const result = await openSaveDialog({
      teams,
      initialName: opts.forceCreate && current ? `${current.name} Kopie` : "",
      submitLabel: opts.forceCreate ? "Als neuen Spielzug speichern" : "Speichern",
    });
    if (!result) {
      return;
    }
    const selectedTeam = result.teamId ? teams.find((team) => team.id === result.teamId) : null;
    const nextState = selectedTeam
      ? { ...state, tokens: applyTeamToTokens(state.tokens, selectedTeam) }
      : state;
    if (selectedTeam) {
      appStore.set({ tokens: nextState.tokens });
    }
    const payload = stateToPlay(
      {
        name: result.name,
        teamId: result.teamId,
        ...(thumbnail ? { thumbnail } : {}),
      },
      nextState,
    );
    const newId = await createPlay(uid, payload);
    setCurrentPlay({ playId: newId, name: result.name, teamId: result.teamId });
    markEditorClean();
    showToast("Neuer Spielzug gespeichert");
  } catch (err) {
    showToast(mapAuthError(err));
    console.warn("[save] failed", err);
  }
}

/** @param {KeyboardEvent} e */
function keyboardHandler(e) {
  // Skip if user is typing in input/textarea/contenteditable
  const target = /** @type {HTMLElement | null} */ (e.target);
  if (
    target &&
    (target.tagName === "INPUT" ||
      target.tagName === "TEXTAREA" ||
      target.tagName === "SELECT" ||
      target.isContentEditable)
  ) {
    return;
  }

  const ctrl = e.ctrlKey || e.metaKey;
  if (ctrl && e.key.toLowerCase() === "z" && !e.shiftKey) {
    e.preventDefault();
    undo();
  } else if (ctrl && ((e.key.toLowerCase() === "z" && e.shiftKey) || e.key.toLowerCase() === "y")) {
    e.preventDefault();
    redo();
  } else if (e.key === "Delete" || e.key === "Backspace") {
    const id = appStore.get().selectedDrawingId;
    if (id) {
      e.preventDefault();
      const cmd = deleteDrawingCommand(id);
      if (cmd) {
        execute(cmd);
      }
    }
  } else if (ctrl && e.key.toLowerCase() === "s") {
    e.preventDefault();
    handleSave().catch((err) => console.warn("[save]", err));
  } else if (e.key === "Escape") {
    const state = appStore.get();
    if (state.selectedDrawingId) {
      appStore.set({ selectedDrawingId: null });
    }
  }
}
